// Shared pieces of the rebounding pages, lifted from the board's design ("Rebounding Web.dc.html") so the upload
// page is built from the same tokens: ground (black / white) palette, pills, the ground toggle.
import React, { useState } from "react";
import { Ground, Palette } from "./reboundingModel";

export const RED = "#CF0A2C";
const TICK = "M4.5 12.5L10 18L19.5 6.5";

export function stored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

/** A stored list (comma-separated), keeping only allowed values. */
export function storedList<T extends string>(key: string, allowed: readonly T[]): T[] {
  try {
    const v = localStorage.getItem(key);
    return v ? (v.split(",").filter((x) => allowed.includes(x as T)) as T[]) : [];
  } catch {
    return [];
  }
}

export function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private windows can refuse storage; the choice still holds for this visit.
  }
}

/** The background choice, shared by the board and the upload page. White unless the viewer picked black (Trevor,
 * 2026-10-02: the design's default was black). */
export function useGround(): [Ground, (g: Ground) => void] {
  const [ground, setGround] = useState<Ground>(() => stored("reb-web-ground", ["Black", "White"] as const, "White"));
  return [
    ground,
    (next: Ground) => {
      store("reb-web-ground", next);
      setGround(next);
    },
  ];
}

/** The design's toggle pill: filled when on, outlined when off. */
export const pill = (P: Palette, on: boolean, size = 18): React.CSSProperties => ({
  height: 34,
  padding: "0 14px",
  border: `1px solid ${on ? P.fg : P.pillBorder}`,
  background: on ? P.fg : "transparent",
  color: on ? P.bg : P.sub,
  fontFamily: "var(--font-display)",
  fontWeight: 800,
  fontSize: size,
  cursor: "pointer",
  borderRadius: 0,
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  textDecoration: "none",
  boxSizing: "border-box",
});

export const squareButton = (P: Palette): React.CSSProperties => ({
  width: 34,
  height: 34,
  border: `1px solid ${P.pillBorder}`,
  background: "transparent",
  cursor: "pointer",
  borderRadius: 0,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const GroundToggle: React.FC<{ ground: Ground; setGround: (g: Ground) => void }> = ({ ground, setGround }) => {
  const swatch = (g: Ground, bg: string, ring: string, stroke: string) => (
    <button
      onClick={() => setGround(g)}
      aria-label={`${g} background`}
      style={{ width: 34, height: 34, border: `2px solid ${ground === g ? RED : ring}`, background: bg, cursor: "pointer", borderRadius: 0, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="3.4" strokeLinecap="square">
        <path d={ground === g ? TICK : ""} />
      </svg>
    </button>
  );
  return (
    <div style={{ display: "flex" }} title="Background">
      {swatch("Black", "#000", "#5A5A5A", "#fff")}
      {swatch("White", "#fff", "#BDBDBD", "#000")}
    </div>
  );
};

/** Eyebrow + display title, the band under the header on both pages. */
export const TitleBlock: React.FC<{ eyebrow: string; title: string }> = ({ eyebrow, title }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".14em", color: RED, whiteSpace: "nowrap" }}>{eyebrow}</span>
    <span style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 36, lineHeight: 0.82 }}>{title}</span>
  </div>
);

export const errorText = (e: any) => e?.response?.data?.error || e?.message || "Something went wrong. Try again.";

/** "2026-09-30" -> "9/30" */
export const md = (iso: string) => {
  const [, m, d] = iso.split("-");
  return `${Number(m)}/${Number(d)}`;
};

/** A stored UTC timestamp in the viewer's time: "Oct 2, 11:42 AM". */
export const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";

/** Who did it, short: the part of an email before the @. */
export const who = (by: string | null) => (by ? by.split("@")[0] : "");

export const label11 = (P: Palette): React.CSSProperties => ({ fontSize: 11, fontWeight: 700, letterSpacing: ".12em", color: P.sub });
export const head14 = (P: Palette): React.CSSProperties => ({ fontSize: 14, fontWeight: 700, letterSpacing: ".12em", color: P.sub });
export const num: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontWeight: 800,
  fontSize: 26,
  lineHeight: 1,
  fontVariantNumeric: "tabular-nums",
};

export type Tone = "good" | "mid" | "bad" | "plain" | "none";
export const toneFill = (P: Palette, t: Tone) => (t === "plain" ? P.fg : t === "none" ? P.none : P[t]);

/** A one-line notice with a coloured left rule, the page's only message style. */
export const Notice: React.FC<{ P: Palette; tone: Tone; children: React.ReactNode }> = ({ P, tone, children }) => (
  <div style={{ borderLeft: `6px solid ${toneFill(P, tone)}`, background: P.zebra, padding: "10px 14px", fontSize: 17, margin: "0 0 10px" }}>
    {children}
  </div>
);
